
interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const maderotherapy: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "madero-therapy-dubai",
        "meta": {
            "title": "Madero Therapy Dubai | Elite Body Home Polyclinic",
            "description": "Achieve perfect body shaping with Madero therapy (wood therapy) at Elite Body Home in Dubai. Non-invasive body contouring, skin tightening and fat loss treatment performed by professionals.",
            "robots": "index, follow",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Madero Therapy Dubai"],
        "hero": {
            "title": "Madero Therapy",
            "subtitle": "Wood Therapy Body Sculpting",
            "description": "Have you ever noticed you have fats in places that you think should be more presentable? Do you want your body shaped perfectly? Don't worry! At Elite Body Home Polyclinic, we can do it with Madero therapy or wood therapy."
        },
        "howItWorks": {
            "title": "What is Madero Therapy and How Does it Work?",
            "description": "Madero therapy (also known as wood therapy) is a holistic massage technique that uses wooden instruments designed for body contouring and sculpting. The process involves rolling wooden rollers and cups on legs, belly, and back to apply pressure. This therapy breaks down localized fat, smoothens cellulite and improves skin elasticity with consistent application."
        },
        "procedureSteps": {
            "heading": "Madero's Therapy: Procedure of Each Step",
            "para": "For effective results, Madero therapy in Dubai encompasses a series of steps:",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Our specialists discuss your body goals/concerns and observe the required results.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "Preparation",
                    "desc": "You lie down comfortably on the treatment table while our therapist applies natural oil to prepare the skin.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Therapy Session",
                    "desc": "Various wooden tools target specific areas with gentle pressure for lymphatic drainage and fat breakdown.",
                    "icon": "fa-solid fa-massage"
                },
                {
                    "title": "Duration",
                    "desc": "Each session lasts 60 minutes. Results can typically be seen within a few sessions.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Madero Treatment",
            "subheading": "All You Need to Know",
            "para": "Madero therapy has many benefits:",
            "items": [
                {
                    "title": "Improved Blood Flow",
                    "desc": "Enhances circulation, oxygenating tissues and promoting natural healing.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Stress Removal",
                    "desc": "Rhythmic massage techniques help release stress and tension.",
                    "icon": "fa-solid fa-cloud"
                },
                {
                    "title": "Better Skin Appearance",
                    "desc": "Tightens skin texture and gives a healthy glow.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "Body Sculpting",
                    "desc": "Helps trim body fat and tone difficult areas like thighs and abdomen.",
                    "icon": "fa-solid fa-person"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit from Madero Therapy?",
            "subheading": "",
            "para": "Madero therapy is good for those seeking body shaping or cellulite reduction. Consult your physician if you have underlying health issues.",
            "items": []
        },
        "preparationTips": {
            "heading": "Preparing for Your Madero Therapy Appointments",
            "subheading": "Steps To Take Before Madero Therapy",
            "para": "",
            "items": [
                {
                    "title": "Hydrate",
                    "desc": "Drink water before your session to help lymphatic drainage.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Avoid Heavy Meals",
                    "desc": "Don't eat large meals 1-2 hours before your session.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Wear Loose Clothing",
                    "desc": "Choose comfortable, loose-fitting clothes for easy treatment access.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "Outcome Anticipated from Madero Therapy",
            "subheading": "",
            "para": "Results vary depending on body type and number of sessions:",
            "items": [
                {
                    "title": "Short-Term Effects",
                    "desc": "Visible reduction of cellulite and skin softening after 3-5 sessions.",
                    "icon": "fa-solid fa-hourglass-start"
                },
                {
                    "title": "Long-Term Effects",
                    "desc": "Skin tightening and body contouring with regular sessions.",
                    "icon": "fa-solid fa-hourglass-end"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Advice For The Best Results",
            "subheading": "",
            "para": "To achieve the best benefits:",
            "items": [
                {
                    "title": "Hydrate After Sessions",
                    "desc": "Helps flush out toxins released during treatment.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "Healthy Diet",
                    "desc": "Maintain a balanced diet to enhance results.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "Consistent Massage",
                    "desc": "Attend scheduled massages regularly for long-term benefits.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Is Madero therapy painful?",
                    "answer": "No, it's a non-invasive treatment using gentle pressure with wooden tools."
                },
                {
                    "question": "How soon can I see results?",
                    "answer": "Most clients notice improvements after 3-5 sessions."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "There may be temporary redness but no major side effects."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "ar": {
        "url": "madero-therapy-dubai",
        "meta": {
            "title": "علاج الماديروثيرابي دبي | Elite Body Home Polyclinic",
            "description": "احصل على نحت الجسم المثالي مع علاج الماديروثيرابي (مساج الخشب) في Elite Body Home بدبي. علاج غير جراحي لنحت الجسم، شد الجلد وتقليل الدهون.",
            "robots": "index, follow",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج الماديروثيرابي دبي"],
        "hero": {
            "title": "الماديروثيرابي",
            "subtitle": "نحت الجسم بمساج الخشب",
            "description": "هل تعاني من تراكم الدهون في بعض المناطق وترغب في تحسين مظهرها؟ هل تحلم بجسم متناسق وجذاب؟ لا داعي للقلق! يمكنك تحقيق ذلك مع علاج الماديروثيرابي في ايليت بودي هوم."
        },
        "howItWorks": {
            "title": "ما هو الماديروثيرابي وكيف يعمل؟",
            "description": "يُعرف الماديروثيرابي أيضًا بمساج الخشب، وهو تقنية تدليك علاجية تستخدم أدوات خشبية مصممة خصيصًا لنحت الجسم وتحديده، حيث يعتمد على تطبيق ضغط باستخدام بكرات وأكواب خشبية على مناطق مثل الفخذين، والبطن، والظهر."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء الماديروثيرابي",
            "para": "يشمل علاج الماديروثيرابي في دبي عدة خطوات لضمان تحقيق نتائج فعالة:",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "تبدأ الجلسة بالاستشارة، حيث تُناقش احتياجاتك وأهدافك الجمالية.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "الاستعداد",
                    "desc": "تستلقى على سرير العلاج في وضعية مريحة، ثم يستخدم المعالج زيتًا طبيعيًا.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "جلسة العلاج",
                    "desc": "تُستخدم أدوات خشبية متخصصة بأحجام وأشكال مختلفة على المناطق المستهدفة.",
                    "icon": "fa-solid fa-massage"
                },
                {
                    "title": "مدة الجلسة",
                    "desc": "تستغرق كل جلسة 60 دقيقة تقريبًا، وتبدأ النتائج بالظهور بعد عدة جلسات.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد الماديروثيرابي",
            "subheading": "",
            "para": "يقدم الماديروثيرابي العديد من الفوائد المدهشة:",
            "items": [
                {
                    "title": "تحسين تدفق الدم",
                    "desc": "يساعد على تنشيط الدورة الدموية في الجسم.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "التخلص من التوتر",
                    "desc": "يمنح إحساسًا عميقًا بالاسترخاء ويخفف التوتر.",
                    "icon": "fa-solid fa-cloud"
                },
                {
                    "title": "تحسين مظهر البشرة",
                    "desc": "يعمل على تحسين مرونة الجلد وتنعيمه.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "نحت الجسم",
                    "desc": "يساعد في تقليل الدهون وشد المناطق الصعبة.",
                    "icon": "fa-solid fa-person"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يعد الماديروثيرابي خيارًا مثاليًا لمن يرغب في نحت الجسم أو تقليل السيلوليت.",
            "items": []
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "لضمان الحصول على أقصى استفادة من جلسة الماديروثيرابي:",
            "items": [
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "اشرب كميات كافية من الماء قبل الجلسة.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "تجنب الوجبات الثقيلة",
                    "desc": "تجنب تناول الوجبات الثقيلة قبل الجلسة بساعتين.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "ارتداء ملابس مريحة",
                    "desc": "اختر ملابس فضفاضة يسهل خلعها أثناء العلاج.",
                    "icon": "fa-solid fa-shirt"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "تختلف نتائج الماديروثيرابي من شخصٍ لآخر:",
            "items": [
                {
                    "title": "نتائج سريعة",
                    "desc": "ستلاحظ تحسن واضح في مظهر السيلوليت بعد 3-5 جلسات.",
                    "icon": "fa-solid fa-hourglass-start"
                },
                {
                    "title": "نتائج طويلة الأمد",
                    "desc": "تساعد الجلسات المنتظمة على شد البشرة وتحسين تناسق القوام.",
                    "icon": "fa-solid fa-hourglass-end"
                }
            ]
        },
        "aftercareTips": {
            "heading": "العناية بعد الجلسة",
            "subheading": "",
            "para": "يُفضل اتباع النصائح التالية:",
            "items": [
                {
                    "title": "الترطيب بعد الجلسة",
                    "desc": "شرب كميات كافية من الماء يساعد في طرد السموم.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "نظام غذائي متوازن",
                    "desc": "يساعد تناول طعام صحي في الحصول على أفضل نتائج.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "الاستمرارية في الجلسات",
                    "desc": "يعزز الالتزام بالجلسات المحددة بانتظام من تأثير العلاج.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل الماديروثيرابي مؤلم؟",
                    "answer": "لا، إنه علاج غير مؤلم يستخدم ضغطًا لطيفًا بأدوات خشبية."
                },
                {
                    "question": "متى يمكنني رؤية النتائج؟",
                    "answer": "معظم العملاء يلاحظون التحسن بعد 3-5 جلسات."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "قد يكون هناك احمرار مؤقت ولكن لا توجد آثار جانبية كبيرة."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/New-Service-Banner-Image-1-1.jpg",
            "beforeAfter": [
                {
                    "src": "https://example.com/before1.jpg",
                    "alt": "Before Madero therapy"
                },
                {
                    "src": "https://example.com/after1.jpg",
                    "alt": "After Madero therapy"
                }
            ],
            "introductionImage": "https://example.com/madero-intro.jpg"
        },
        "product": {
            "id": 2,
            "title": "Madero Therapy",
            "price": 399,
            "description": "Non-invasive body sculpting using wooden tools for contouring, skin tightening and fat reduction."
        }
    }
}